<template>
  <div v-if="getOfferErrorMessage && shouldWaitForCx">
    <CxWaitValidation />
  </div>
  <div v-else class="offers">
    <div class="m-1">
      <div class="row" v-if="loadingSelectedDevice">
        <div class="col-md-12">
          <div class="row">
            <div class="col first-spinner">
              <Spinner />
            </div>
          </div>
        </div>
      </div>
      <div class="row d-flex justify-content-center align-items-center" v-else>
        <div class="col-md-12 cool-gray-bg">
          <Transition name="list">
            <DevicePreview
              v-if="selectedImageUrl"
              :imageUrl="selectedImageUrl"
              :deviceName="deviceModelName"
              :currency="currency"
              :locale="merchantLocale"
              :price="priceWithSalesTax"
            />
          </Transition>
        </div>
        <div class="col-md-12">
          <div class="row" v-if="pricingLoader">
            <div class="col">
              <Spinner />
            </div>
          </div>
          <div class="mt-3" v-if="displayPaymentTerms">
            <div class="row">
              <div class="col text-center">
                <h3 class="payment-terms">
                  {{ t("PAYMENT_TERMS") }}
                </h3>
              </div>
            </div>
            <Transition name="list">
              <PaymentTerms
                @onSelect="setSelectedOffer"
                :disable-offer-button="disableOfferButton"
                :selected-offer="selectedOffer"
                :preApprovalState="preApprovalState"
                :use-responsive-layout="true"
              />
            </Transition>
          </div>
        </div>
        <div class="col-md-12 d-flex justify-content-evenly align-items-center">
          <div class="col fw-bold px-5">
            {{ t("DOWN_PAYMENT") }}:
            <p>{{ t("TO_BE_PAID_TODAY") }}</p>
          </div>
          <div class="col px-5">
            <div class="float-end mr-5">
              {{ formatCurrencyFromMerchant(downPayment) }}
            </div>
          </div>
          <div
            v-if="!isMobileDevice"
            class="col ml-4"
            v-show="!loadingSelectedDevice"
          >
            <div>
              <ButtonContinue
                class="btn-next"
                :isEnabled="buttonContinue"
                :label="t('CONTINUE')"
                @callback="postSelectOffers"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="isMobileDevice"
        class="row mt-3"
        v-show="!loadingSelectedDevice"
      >
        <div class="col-md-12 py-2">
          <ButtonContinue
            class="btn-next"
            :isEnabled="buttonContinue"
            :label="t('CONTINUE')"
            @callback="postSelectOffers"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import Spinner from "@/apps/merchant3/shared-components/ui/Spinner.vue";
import DevicePreview from "@/apps/merchant3/components/offers/DevicePreview.vue";
import PaymentTerms from "@/apps/merchant3/components/offers/PaymentTerms.vue";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import {
  computed,
  ComputedRef,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  watch,
  watchEffect,
} from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useStore } from "vuex";
import useCurrency, { ICurrencyType } from "@/apps/merchant3/shared-hooks/useCurrency";
import { ApprovalState } from "@/apps/merchant3/types/OBR";
import scrollToBottom from "@/apps/merchant3/shared-helpers/scrollToBottom";
import {
  putOffers,
  setPrice,
  getPricing,
  isCsAvailable,
} from "@/apps/merchant3/services/carts.service";
import { AxiosError, AxiosResponse } from "axios";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { isBrazilSaleFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import { createBankProposal } from "@/apps/merchant3/services/bank_service";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import { getWelcomeCallStatus } from "@/apps/merchant3/services/contract.service";

import IWelcomeCall from "@/apps/merchant3/types/IWelcomeCall";
import {
  logPostSelectOffers,
  logSendLatestCart,
} from "@/apps/merchant3/helpers/offers/loggers.helper";
import { useRouter } from "vue-router";
import useAsset from "@/apps/merchant3/shared-helpers/useAsset";
import useIntervalCustomerState from "@/apps/merchant3/hooks/useIntervalCustomerState";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import { logEcommerceEvent } from "@/apps/merchant3/helpers/offers/ecommerce-event-log";
import { isMobile } from "@/apps/merchant3/shared-helpers/browser";
import CxWaitValidation from "@/apps/merchant3/components/CxWaitValidation.vue";
import IErrorResponse from "@/apps/merchant3/types/IErrorResponse";
import useLocale from "@/apps/merchant3/shared-hooks/useLocale";
import { formatCurrencyFromMerchant } from "@/apps/merchant3/shared-helpers/format-currency.helper";
import { trackEvent } from "@/apps/merchant3/helpers/pixel.helper";
import { pixelEventsEnum } from "@/apps/merchant3/enums/pixelEventsEnum";
import { trackMarketingEvent } from "@/apps/merchant3/services/marketing-events.service";
import MarketingEvents from "@/apps/merchant3/types/MarketingEvents";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { getCurrency } = useCurrency();
const { getLocale } = useLocale();
const buttonContinue: Ref<boolean> = ref(false);
const disableOfferButton: Ref<boolean> = ref(false);
const displayPaymentTerms: Ref<boolean> = ref(false);
const errorMessage: Ref<string> = ref("");
const isMobileDevice: Ref<boolean> = ref(isMobile());
const loadingSelectedDevice: Ref<boolean> = ref(true);
const preApprovalState: Ref<ApprovalState> = ref(ApprovalState.PENDING);
const pricingLoader: Ref<boolean> = ref(true);
const selectedOffer: Ref<number> = ref(0);
const cartTag: ComputedRef<string> = computed(() => {
  return store.state.carts.cartTag || "";
});

const currency: ComputedRef<ICurrencyType> = computed(() => {
  return getCurrency(store.state.merchant?.merchant?.country);
});
const merchantLocale = computed(() => {
  return store.state.merchant?.merchant?.country
    ? getLocale(store.state.merchant.merchant.country).localeWeb
    : "es-MX";
});

const deviceModelName: ComputedRef<string> = computed(() => {
  return store.state.offers.selectedDeviceModel.name || "";
});

const downPayment: ComputedRef<number | string> = computed(() => {
  return store.state.carts.downPayment || "";
});
const getOfferErrorMessage = computed(() => {
  return store.state.offers.offerErrorMessage;
});

const idAnnotations: ComputedRef<string | null> = computed(
  () => store.state.customer.idAnnotations
);
const selectedImageUrl: ComputedRef<string | null> = computed(() => {
  return store.state.offers.selectedDeviceImageUrl;
});

const shouldWaitForCx: ComputedRef<boolean> = computed(() => {
  return store.state.offers.shouldWaitForCx;
});

const priceWithSalesTax: ComputedRef<number> = computed(() => {
  return store.state.carts?.priceWithSalesTax || 0;
});
const intervalCsAvail = ref<number | null>(null);
const checkIfReviewable = () => {
  if (shouldWaitForCx.value) {
    isCsAvailable(store.state.carts.cartTag)
      .then(
        (response: AxiosResponse<{ available: string | boolean | null }>) => {
          const available = response.data.available;
          if (!available) {
            logEcommerceEvent(
              EventLogs.Events.ECOMMERCE_CART_TERMINATE_SALE_CUSTOMER_INFO
            );
            return router.push({
              name: RouteName.M3RejectedOfferEcommerce,
              params: { countryCode: getCountryCode() },
            });
          }
        }
      )
      .catch((error: AxiosError<IErrorResponse>) => {
        errorMessage.value = getAPIErrorMessage(error.response?.data, t);
      });
  }
};
intervalCsAvail.value = setInterval(() => {
  checkIfReviewable();
}, 15000);

watch(
  [idAnnotations, shouldWaitForCx],
  ([newValueId], [oldValueId, oldValueCX]) => {
    if (
      newValueId !== oldValueId &&
      oldValueCX !== undefined &&
      store.state.carts.cartTag
    ) {
      store.dispatch("offers/loadManufacturer", store.state.carts.cartTag);
    }
  },
  { immediate: true }
);

watchEffect(() => {
  if (store.state.offers.offerErrorMessage && !shouldWaitForCx.value) {
    logEcommerceEvent(EventLogs.Events.ECOMMERCE_OFFERS_SELECTION_ERROR);
    router.push({ name: RouteName.M3RejectedOfferEcommerce, params: { countryCode: getCountryCode() } });
  }
});

onUnmounted(() => {
  if (intervalCsAvail.value) {
    clearInterval(intervalCsAvail.value);
  }
});
const checkWelcomeCallStatus = () => {
  getWelcomeCallStatus(store.state.carts.cartTag)
    .then((response: AxiosResponse<unknown>) => {
      const { welcomeCallRequired } = response.data as IWelcomeCall;

      store.dispatch("carts/setWelcomeCallRequired", welcomeCallRequired);
    })
    .catch((error) => {
      errorMessage.value = getAPIErrorMessage(error.response?.data, t);
    });
};

const postSelectOffers = () => {
  checkWelcomeCallStatus();
  logPostSelectOffers(selectedOffer.value, store);
  trackEvent(pixelEventsEnum.OfferSelection, {
    cartId: store.state.carts.cartTag,
  });
  putOffers(cartTag.value, store.state.carts.months).then(
    (response: AxiosResponse) => {
      const cart = response.data.cart;
      store.dispatch("carts/setCart", cart);
      logSendLatestCart(cart);
      logEcommerceEvent(EventLogs.Events.ECOMMERCE_OFFERS_SELECTION);
      if (isBrazilSaleFlow()) {
        createBankProposal(cartTag.value)
          .then(() => {
            logEvent(EventLogs.Events.BANK_PROPOSAL_CREATED);
            router.push({ name: RouteName.M3EcommerceSummary, params: { countryCode: getCountryCode() } });
          })
          .catch((error) => {
            errorMessage.value = getAPIErrorMessage(error.response?.data, t);
          });
      } else {
        router.push({ name: RouteName.M3EcommerceSummary, params: { countryCode: getCountryCode() } });
      }
    },
    (error: AxiosError) => {
      if (error) console.log("ERROR_SOMETHING_GOES_WRONG");
    }
  );
};
const setSelectedOffer = (value: number) => {
  store.dispatch("carts/setSelectedOffer", value);
  selectedOffer.value = value;
  buttonContinue.value = true;
  scrollToBottom();
};
const cartManufacturer: ComputedRef<string | null> = computed(
  () => store.state.carts.manufacturer
);
const cartDeviceFamilyCode: ComputedRef<number | null> = computed(
  () => store.state.carts.deviceFamilyId
);
const cartDeviceFamilyName: ComputedRef<number | null> = computed(
  () => store.state.carts.deviceFamilyName
);

const cartDeviceModelCode: ComputedRef<number | null> = computed(
  () => store.state.carts.deviceModelId
);
const cartDeviceModelName: ComputedRef<string | null> = computed(
  () => store.state.carts.friendlyDeviceName
);

const cartPriceWithSalesTax: ComputedRef<number | null> = computed(
  () => store.state.carts.priceWithSalesTax
);
const setDeviceModel = async () => {
  await store.dispatch("offers/setSelectedManufacturer", {
    name: cartManufacturer.value,
    code: cartManufacturer.value,
  });
  await store.dispatch("offers/setSelectedDeviceFamily", {
    name: cartDeviceFamilyName.value,
    code: cartDeviceFamilyCode.value,
  });
  const deviceModelPayload = {
    name: cartDeviceModelName.value,
    code: cartDeviceModelCode.value,
    description: "",
  };
  await store.dispatch("offers/setSelectedDeviceModel", deviceModelPayload);

  await store.dispatch(
    "offers/setSelectedImageUrl",
    useAsset("img/phone-preview.png")
  );

  if (cartPriceWithSalesTax.value) {
    await setPrice(cartTag.value, cartPriceWithSalesTax.value).then(
      async (response: AxiosResponse) => {
        await store.dispatch("carts/setCart", response.data.cart);
        await store.dispatch("carts/setDownPayment", response.data.downPayment);
        await getPricing(cartTag.value).then((response: AxiosResponse) => {
          store.dispatch("carts/setCart", response.data.cart);
          store.dispatch("carts/updatePricing", response.data.pricing);
        });
      },
      async (error: AxiosError) => {
        if (error) {
          await logEcommerceEvent(
            EventLogs.Events.ECOMMERCE_ERROR_SETTING_PRICE
          );
          await router.push({ name: RouteName.M3RejectedOfferEcommerce, params: { countryCode: getCountryCode() } });
        }
      }
    );
  }
  await store.dispatch("offers/setSelectedDeviceModelObject", {
    deviceModelId: cartDeviceModelCode.value,
    minRetailPrice: cartPriceWithSalesTax.value,
    maxRetailPrice: cartPriceWithSalesTax.value,
    minFinanceAmountIncrement: 1,
  });
};
onMounted(() => {
  useIntervalCustomerState();
  useCartPersistState((): boolean => {
    store.dispatch("offers/loadManufacturer", store.state.carts.cartTag);
    return true;
  });
  useMerchantPersistState(() => true);

  // Check if offer has already been selected previoulsy and set it
  if (store.state.carts.months) {
    setSelectedOffer(store.state.carts.months);
  }
});

const handleManufacturerChange = (manufacturer: string | null) => {
  if (manufacturer) {
    trackEvent(pixelEventsEnum.RiskApproved, {
      cartId: store.state.carts.cartTag,
    });
    trackMarketingEvent(MarketingEvents.RISK_APPROVED);
    setDeviceModel().then(() => {
      loadingSelectedDevice.value = false;
      pricingLoader.value = false;
      displayPaymentTerms.value = true;
    });
  }
};

watchEffect(() => {
  handleManufacturerChange(store.state.offers.manufacturer);
});
</script>
<style scoped lang="scss">
@import "@/assets/scss/_constants.scss";
.first-spinner {
  color: inherit;
}
.responsive-width {
  width: 50%;
}
@media (max-width: 768px) {
  .responsive-width {
    width: 100%;
  }
}
.cool-gray-bg {
  background: rgba(237, 237, 237, 1);
}
</style>
